import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend
} from 'recharts';
import { Users, FileQuestion, Phone, TrendingUp, Divide } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { fetchJson, type DashboardData, type DashboardGroupBy, ApiError } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

function StatCard({
  title,
  value,
  icon
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function StatusBreakdownCard({ data }: { data: DashboardData }) {
  const { completed, abandoned, inProgress, total, completionRate } = data.statusBreakdown;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">סטטוס רשומות · {data.currentLabel}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">אחוז השלמה</span>
            <span className="text-2xl font-semibold">{completionRate}%</span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all"
              style={{ width: `${completionRate}%` }}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <Badge variant="success">הושלמו: {completed}</Badge>
          <Badge variant="muted">ננטשו: {abandoned}</Badge>
          {inProgress > 0 && <Badge variant="muted">בתהליך: {inProgress}</Badge>}
          <Badge variant="outline">סה"כ: {total}</Badge>
        </div>
      </CardContent>
    </Card>
  );
}

function PerQuestionnaireCard({ data }: { data: DashboardData }) {
  const rows = data.perQuestionnaire;
  const max = rows.reduce((m, r) => Math.max(m, r.sessions), 0) || 1;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">רשומות לפי שאלון · {data.currentLabel}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">אין רשומות בתקופה זו.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate">{r.name}</span>
                  <span className="font-semibold tabular-nums">{r.sessions}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${(r.sessions / max) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<DashboardGroupBy>('month');

  const load = useCallback((mode: DashboardGroupBy) => {
    return fetchJson<DashboardData>(`/api/dashboard?groupBy=${mode}`);
  }, []);

  useEffect(() => {
    let cancelled = false;
    load(groupBy)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: ApiError) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [groupBy, load]);

  const recentColumns: DataTableColumn<DashboardData['recentSessions'][number]>[] = useMemo(
    () => [
      { key: 'customerName', header: 'שם לקוח', cell: (s) => s.customerName || '-', exportValue: (s) => s.customerName || '' },
      {
        key: 'phone',
        header: 'טלפון',
        cell: (s) => (
          <span className="font-mono text-xs" dir="ltr">
            {s.customerPhone || s.callerPhone || ''}
          </span>
        ),
        exportValue: (s) => s.customerPhone || s.callerPhone || ''
      },
      {
        key: 'questionnaireName',
        header: 'שאלון',
        cell: (s) => s.questionnaireName || '-',
        exportValue: (s) => s.questionnaireName || ''
      },
      {
        key: 'startedAt',
        header: 'תאריך',
        cell: (s) => formatDateTime(s.startedAt),
        exportValue: (s) => formatDateTime(s.startedAt)
      },
      {
        key: 'status',
        header: 'סטטוס',
        cell: (s) => (
          <Badge variant={s.status === 'completed' ? 'success' : 'muted'}>
            {s.status === 'completed' ? 'הושלם' : s.status === 'abandoned' ? 'נטוש' : s.status}
          </Badge>
        ),
        exportValue: (s) => (s.status === 'completed' ? 'הושלם' : s.status === 'abandoned' ? 'נטוש' : s.status)
      }
    ],
    []
  );

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>שגיאה</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  if (!data) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  const showSum = data.currentSum !== null;
  const showAvg = showSum && data.avgSumPerRecord !== null;
  const periodWord = data.groupBy === 'cycle' ? 'מחזור' : 'חודש';
  const chartTitle =
    data.groupBy === 'cycle' ? '12 המחזורים האחרונים' : 'פעילות 12 חודשים אחרונים';

  // 3 base cards (+ sum, + avg). Fit them all on one row on wide screens.
  const statCount = 3 + (showSum ? 1 : 0) + (showAvg ? 1 : 0);
  const statGridCols =
    statCount >= 5 ? 'lg:grid-cols-5' : statCount === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">לוח בקרה</h1>
          <p className="text-sm text-muted-foreground">סקירה כללית של פעילות הארגון</p>
        </div>
        <Tabs value={data.groupBy} onValueChange={(v) => setGroupBy(v as DashboardGroupBy)}>
          <TabsList>
            <TabsTrigger value="month">לפי חודש</TabsTrigger>
            <TabsTrigger value="cycle">לפי מחזור</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${statGridCols}`}>
        <StatCard title="לקוחות" value={data.customerCount} icon={<Users className="h-4 w-4" />} />
        <StatCard
          title={`רשומות ${data.currentLabel}`}
          value={data.currentSessions}
          icon={<Phone className="h-4 w-4" />}
        />
        <StatCard
          title="שאלונים פעילים"
          value={data.activeQuestionnairesCount}
          icon={<FileQuestion className="h-4 w-4" />}
        />
        {showSum && (
          <StatCard
            title={`סכום ${periodWord} (${data.currentLabel})`}
            value={(data.currentSum ?? 0).toLocaleString('he-IL')}
            icon={<TrendingUp className="h-4 w-4" />}
          />
        )}
        {showAvg && (
          <StatCard
            title="ממוצע סכום לרשומה"
            value={Math.round(data.avgSumPerRecord ?? 0).toLocaleString('he-IL')}
            icon={<Divide className="h-4 w-4" />}
          />
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{chartTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          {data.chart.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              עדיין אין מחזורים להצגה.
            </p>
          ) : (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} reversed={true} />
                  <YAxis tick={{ fontSize: 12 }} orientation="right" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="sessions" fill="hsl(var(--primary))" name="רשומות" radius={[4, 4, 0, 0]} />
                  {showSum && (
                    <Bar dataKey="sum" fill="hsl(var(--secondary))" name="סכום" radius={[4, 4, 0, 0]} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <StatusBreakdownCard data={data} />
        <PerQuestionnaireCard data={data} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>5 רשומות אחרונות</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={recentColumns}
            data={data.recentSessions}
            getRowKey={(s) => s.id}
            emptyMessage="עדיין אין רשומות."
            exportFileName="רשומות-אחרונות"
            exportSheetName="רשומות אחרונות"
          />
        </CardContent>
      </Card>
    </div>
  );
}
